import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { Play, Activity, BarChart2, Clock, ChevronDown, ChevronUp, Trash2, Pencil, Check, X, RefreshCw, Save } from 'lucide-react';
import ScoreVersionSelector from '../components/ScoreVersionSelector';
import PortfolioProfileToggle, { DEFAULT_PORTFOLIO_PROFILES, normalizeProfiles } from '../components/PortfolioProfileToggle';
import { shuffledPunchlineLoadingPhases, PUNCHLINE_LOADING_PHASES } from '../utils/punchlineLoadingText';
import { API_BASE_URL as API_BASE } from '../apiBase';
import Results from './backtest/Results';
import { DEFAULT_CAD_PER_USD, fmtMoney, fmtPct } from './backtest/shared';
import { ddBasisLabel } from '../utils/format';

const PORTFOLIO_PROFILE_STORAGE_KEY = 'backtestPortfolioProfile20260523';

// ── Default advanced params (optimal strategy) ────────────────────────────────
// Updated 2026-04-28 (v27 H5_HOLD15_H40 winner): 22-now +55B%, DD-C 73.5%
// after path-dependent SL P&L bug fix and joint Bayesian optimization.
// See experiments/v27_optimization/FINAL_RECOMMENDATION.md for details.
// Fallback defaults — used until /api/strategy/config returns. Mirror current
// production values so the UI is functional pre-fetch and if the endpoint is
// unreachable. The defaultsFromCfg() helper below replaces these with values
// fetched from strategy_config.py — single source of truth.
// FALLBACK ONLY — used when /api/strategy/config fetch fails. Live values
// come from defaultsFromCfg(j['30dte']) below. Keep these synced with
// strategy_config.py STRATEGY_30DTE so a fetch failure doesn't show wildly
// stale defaults. Last updated 2026-06-02 for v70 Apex HOLD portfolio config.
const DEFAULT_ADVANCED = {
  tp_pct:               10,        // tpsl_refine ship 2026-08-10 (scalp-and-dead-hold, base==stress)
  tp_stress_pct:        30,        // v70 Apex
  sl_pct:               100,       // tpsl_refine ship 2026-08-10 (disaster stop; deep fires reroute to dead-hold)
  sl_stress_pct:        70,        // v70 Apex
  breadth_thresh:       40,        // inert now (base==stress)
  put_tp_pct:           35,
  put_sl_pct:           20,
  put_sl_hold_default:  0,
  put_sl_hold_monday:   0,
  breadth_adaptive:     true,
  hard_sell_day:        15,
  hard_sell_loss_pct:   40,
  max_positions:        14,        // v70 Apex (full pool, calls-only)
  max_positions_call:   14,
  max_positions_put:    0,
  practical_exposure_enabled: true,
  practical_capital_ceiling: 0,    // v70 Apex UNCAPPED (max compounding; Core/Sentinel cap)
  gross_premium_cap:    50,        // v70 Apex exposure peak (50% > 65% > off)
  call_premium_cap:     50,
  put_premium_cap:      0,         // puts off
  opp_sat_call_ref:     16,
  opp_sat_put_ref:      4,
  opp_sat_power:        0.50,
  opp_sat_floor:        0.55,
  alloc_95plus:         20,        // v70 Apex 75+ full cascade (HOLD makes 75+ tradeable)
  alloc_85_94:          15,
  alloc_80_84:          10,
  alloc_75_79:          10,
  alloc_70_74:          0,
  alloc_p15:            0,         // puts OFF (net-negative on honest scores)
  alloc_p16_20:         0,
  alloc_p21_25:         0,
  breadth_alloc_enabled: true,
  f3f_call_thresh:      50,
  f3f_call_floor:       0.50,
  f3f_call_low:         30,        // was 20; H3 ship Phase 8/9
  f3f_put_thresh:       75,
  f3f_put_floor:        0.50,
  f3f_put_high:         95,
  dd_soft_lo:           0.35,      // v60 recalibration (30 DTE)
  dd_soft_hi:           0.55,
  dd_soft_floor:        0.40,
  rxdd_enabled:         true,      // RXDD VIX-band call dampener (30 DTE, ship 2026-06-04)
  rxdd_vix_c:           22.701,
  rxdd_vix_w:           3.14,
  rxdd_depth:           0.447,
  rxdd_dd_min:          0.077,
  mwdd_enabled:         true,      // MWDD McClellan flat-band call dampener (30 DTE, ship 2026-06-05)
  mwdd_mcc_c:           -0.336,
  mwdd_mcc_w:           22.185,
  mwdd_depth:           0.337,
  mwdd_dd_min:          0.128,
  mwdd_vix_panic:       28.0,
  tvdd_enabled:         true,      // TVDD TRIN volume-flow neutral-band call dampener (30 DTE, ship 2026-06-07)
  tvdd_trin_c:          1.042,
  tvdd_trin_w:          0.268,
  tvdd_depth:           0.426,
  tvdd_dd_min:          0.291,
  tvdd_vix_panic:       28.0,
  bdiv_enabled:         true,      // BDIV pre-top breadth-divergence-at-highs call dampener (30 DTE, ship 2026-06-10)
  bdiv_prox_cut:        0.0198,
  bdiv_prox_full:       0.0075,
  bdiv_gap_c:           7.716,
  bdiv_gap_w:           3.4571,
  bdiv_depth:           0.53,
  svr_enabled:          true,      // SVR semivol_r skew-bridge entry filter (30 DTE, ship 2026-06-05)
  svr_lo_cut:           0.50,
  svr_lo_full:          0.70,
  svr_hi_full:          1.25,
  svr_hi_cut:           1.65,
  svr_floor:            0.50,
  spread_tilt_enabled:  true,      // SPREAD_TILT 75-79 component-spread call alloc haircut (30 DTE, ship 2026-06-15)
  spread_tilt_lo:       26.9,
  spread_tilt_hi:       31.3,
  spread_tilt_depth:    0.40,
  liquidity_floor:      0,         // LIQUIDITY_FLOOR floor killed 2026-08-11 by the exposure-matched control (experiments/floor_mc_2026_08); knob stays 0.0
  // CTSL — Counter-Trend Score Lift (Stage 1 winner, shipped 2026-05-08)
  // Read-only display; backend reads from strategy_config.
  ctsl_enabled:         true,
  ctsl_call_trend_max:  15,
  ctsl_call_target:     98.4,
  ctsl_call_alpha:      0.56,
  ctsl_call_tier_floor: 74.7,
  ctsl_put_trend_min:   76,
  ctsl_put_target:      -0.13,
  ctsl_put_alpha:       0.83,
  ctsl_put_tier_ceiling: 27.9,
};

// Map /api/strategy/config response (one DTE) to DEFAULT_ADVANCED shape.
// `cfg` is `j['30dte']` or `j['15dte']` from the endpoint — see api.py
// get_strategy_config() and strategy_config.to_json_dict().
function defaultsFromCfg(cfg) {
  if (!cfg) return DEFAULT_ADVANCED;
  const opt = cfg.option;
  return {
    tp_pct:               Math.round(opt.TP_BASE * 100),
    tp_stress_pct:        Math.round(opt.TP_STRESS * 100),
    sl_pct:               Math.round(Math.abs(opt.SL_BASE) * 100),
    sl_stress_pct:        Math.round(Math.abs(opt.SL_STRESS) * 100),
    breadth_thresh:       opt.BREADTH_THRESHOLD,
    put_tp_pct:           Math.round(opt.PUT_TP * 100),
    put_sl_pct:           Math.round(Math.abs(opt.PUT_SL) * 100),
    put_sl_hold_default:  opt.PUT_SL_HOLD_BARS_DEFAULT,
    put_sl_hold_monday:   opt.PUT_SL_HOLD_BARS_MONDAY,
    breadth_adaptive:     true,
    hard_sell_day:        cfg.HOLD_DAYS,
    hard_sell_loss_pct:   Math.round(Math.abs(cfg.HARD_SELL_LOSS) * 100),
    max_positions:        cfg.MAX_POSITIONS,
    max_positions_call:   cfg.MAX_POSITIONS_CALL,
    max_positions_put:    cfg.MAX_POSITIONS_PUT,
    practical_exposure_enabled: cfg.PRACTICAL_EXPOSURE_ENABLED,
    practical_capital_ceiling: cfg.PRACTICAL_CAPITAL_CEILING,
    gross_premium_cap:    Math.round((cfg.GROSS_PREMIUM_CAP || 0) * 100),
    call_premium_cap:     Math.round((cfg.CALL_PREMIUM_CAP || 0) * 100),
    put_premium_cap:      Math.round((cfg.PUT_PREMIUM_CAP || 0) * 100),
    opp_sat_call_ref:     cfg.OPP_SAT_CALL_REF,
    opp_sat_put_ref:      cfg.OPP_SAT_PUT_REF,
    opp_sat_power:        cfg.OPP_SAT_POWER,
    opp_sat_floor:        cfg.OPP_SAT_FLOOR,
    alloc_95plus:         Math.round(cfg.TIER_ALLOC.ultra    * 100),
    alloc_85_94:          Math.round(cfg.TIER_ALLOC.top      * 100),
    alloc_80_84:          Math.round(cfg.TIER_ALLOC.mid      * 100),
    alloc_75_79:          Math.round(cfg.TIER_ALLOC.low      * 100),
    alloc_70_74:          Math.round(cfg.TIER_ALLOC.overflow * 100),
    alloc_p15:            Math.round(cfg.PUT_TIER_ALLOC.put_top * 100),
    alloc_p16_20:         Math.round(cfg.PUT_TIER_ALLOC.put_mid * 100),
    alloc_p21_25:         Math.round(cfg.PUT_TIER_ALLOC.put_low * 100),
    breadth_alloc_enabled: cfg.BREADTH_ALLOC_ENABLED,
    f3f_call_thresh:      cfg.F3F_CALL_THRESH,
    f3f_call_floor:       cfg.F3F_CALL_FLOOR,
    f3f_call_low:         cfg.F3F_CALL_LOW,
    f3f_put_thresh:       cfg.F3F_PUT_THRESH,
    f3f_put_floor:        cfg.F3F_PUT_FLOOR,
    f3f_put_high:         cfg.F3F_PUT_HIGH,
    dd_soft_lo:           cfg.DD_SOFT_BAND_LO,
    dd_soft_hi:           cfg.DD_SOFT_BAND_HI,
    dd_soft_floor:        cfg.DD_SOFT_CALL_FLOOR,
    rxdd_enabled:         cfg.RXDD_ENABLED,
    rxdd_vix_c:           cfg.RXDD_VIX_C,
    rxdd_vix_w:           cfg.RXDD_VIX_W,
    rxdd_depth:           cfg.RXDD_DEPTH,
    rxdd_dd_min:          cfg.RXDD_DD_MIN,
    mwdd_enabled:         cfg.MWDD_ENABLED,
    mwdd_mcc_c:           cfg.MWDD_MCC_C,
    mwdd_mcc_w:           cfg.MWDD_MCC_W,
    mwdd_depth:           cfg.MWDD_DEPTH,
    mwdd_dd_min:          cfg.MWDD_DD_MIN,
    mwdd_vix_panic:       cfg.MWDD_VIX_PANIC,
    tvdd_enabled:         cfg.TVDD_ENABLED,
    tvdd_trin_c:          cfg.TVDD_TRIN_C,
    tvdd_trin_w:          cfg.TVDD_TRIN_W,
    tvdd_depth:           cfg.TVDD_DEPTH,
    tvdd_dd_min:          cfg.TVDD_DD_MIN,
    tvdd_vix_panic:       cfg.TVDD_VIX_PANIC,
    bdiv_enabled:         cfg.BDIV_ENABLED,
    bdiv_prox_cut:        cfg.BDIV_PROX_CUT,
    bdiv_prox_full:       cfg.BDIV_PROX_FULL,
    bdiv_gap_c:           cfg.BDIV_GAP_C,
    bdiv_gap_w:           cfg.BDIV_GAP_W,
    bdiv_depth:           cfg.BDIV_DEPTH,
    svr_enabled:          cfg.SVR_ENABLED,
    svr_lo_cut:           cfg.SVR_LO_CUT,
    svr_lo_full:          cfg.SVR_LO_FULL,
    svr_hi_full:          cfg.SVR_HI_FULL,
    svr_hi_cut:           cfg.SVR_HI_CUT,
    svr_floor:            cfg.SVR_FLOOR,
    spread_tilt_enabled:  cfg.SPREAD_TILT_ENABLED,
    spread_tilt_lo:       cfg.SPREAD_TILT_LO,
    spread_tilt_hi:       cfg.SPREAD_TILT_HI,
    spread_tilt_depth:    cfg.SPREAD_TILT_DEPTH,
    liquidity_floor:      cfg.LIQUIDITY_FLOOR,
    // CTSL — Stage 1 winner shipped 2026-05-08; read-only display.
    ctsl_enabled:         cfg.CTSL_ENABLED,
    ctsl_call_trend_max:  cfg.CTSL_CALL_TREND_MAX,
    ctsl_call_target:     cfg.CTSL_CALL_TARGET,
    ctsl_call_alpha:      cfg.CTSL_CALL_ALPHA,
    ctsl_call_tier_floor: cfg.CTSL_CALL_TIER_FLOOR,
    ctsl_put_trend_min:   cfg.CTSL_PUT_TREND_MIN,
    ctsl_put_target:      cfg.CTSL_PUT_TARGET,
    ctsl_put_alpha:       cfg.CTSL_PUT_ALPHA,
    ctsl_put_tier_ceiling: cfg.CTSL_PUT_TIER_CEILING,
  };
}

function applyPortfolioProfileToAdv(base, profile) {
  const params = profile?.params || {};
  const next = { ...base };
  const pct = (value) => Math.round(Number(value || 0) * 100);
  if (params.max_positions != null) next.max_positions = params.max_positions;
  if (params.call_max != null) next.max_positions_call = params.call_max;
  if (params.put_max != null) next.max_positions_put = params.put_max;
  if (params.practical_enabled != null) next.practical_exposure_enabled = !!params.practical_enabled;
  if (params.capital_ceiling != null) next.practical_capital_ceiling = params.capital_ceiling;
  if (params.gross_cap != null) next.gross_premium_cap = pct(params.gross_cap);
  if (params.call_cap != null) next.call_premium_cap = pct(params.call_cap);
  if (params.put_cap != null) next.put_premium_cap = pct(params.put_cap);
  if (params.call_ref != null) next.opp_sat_call_ref = params.call_ref;
  if (params.put_ref != null) next.opp_sat_put_ref = params.put_ref;
  if (params.sat_power != null) next.opp_sat_power = params.sat_power;
  if (params.sat_floor != null) next.opp_sat_floor = params.sat_floor;
  if (params.dd_lo != null) next.dd_soft_lo = params.dd_lo;
  if (params.dd_hi != null) next.dd_soft_hi = params.dd_hi;
  if (params.dd_floor != null) next.dd_soft_floor = params.dd_floor;
  if (params.tier_ultra != null) next.alloc_95plus = pct(params.tier_ultra);
  if (params.tier_top != null) next.alloc_85_94 = pct(params.tier_top);
  if (params.tier_mid != null) next.alloc_80_84 = pct(params.tier_mid);
  if (params.tier_low != null) next.alloc_75_79 = pct(params.tier_low);
  if (params.tier_overflow != null) next.alloc_70_74 = pct(params.tier_overflow);
  if (params.put_top != null) next.alloc_p15 = pct(params.put_top);
  if (params.put_mid != null) next.alloc_p16_20 = pct(params.put_mid);
  if (params.put_low != null) next.alloc_p21_25 = pct(params.put_low);
  return next;
}

// Derive the main-form signal params a profile implies from its (profiled) adv:
// puts-off (no put allocation) => Calls Only; the min call score = the lowest
// FUNDED call tier (Sentinel zeroes the 75-79/80-84 tiers => 85+-only => min 85).
function signalParamsFromAdv(adv) {
  const callsOnly = !(
    Number(adv.alloc_p15) || Number(adv.alloc_p16_20) || Number(adv.alloc_p21_25)
  );
  const minCall = Number(adv.alloc_75_79) > 0 ? 75
                : Number(adv.alloc_80_84) > 0 ? 80
                : Number(adv.alloc_85_94) > 0 ? 85
                : Number(adv.alloc_95plus) > 0 ? 95 : 75;
  return { calls_only: callsOnly, min_score: minCall };
}

// ── Formatters ────────────────────────────────────────────────────────────────
// fmtMoney/fmtPct now live in ./backtest/shared (imported above) -- needed
// both here (SavedRunsPanel's saved-run preview row, the practical-capital
// tooltip) and in the extracted Results tree. fmtPnl was only ever used by
// TradeLog, which moved into backtest/Results.js -- it's defined locally
// there now.

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}
function startOfYearStr() {
  return `${new Date().getFullYear()}-01-01`;
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function SegmentedDateInput({ value, onChange, className }) {
  const yyRef = useRef(null);
  const mmRef = useRef(null);
  const ddRef = useRef(null);

  const parse = (v) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v || '');
    if (!m) return { yy: '', mm: '', dd: '' };
    return { yy: m[1].slice(2), mm: m[2], dd: m[3] };
  };

  const [seg, setSeg] = useState(() => parse(value));

  useEffect(() => {
    setSeg(parse(value));
  }, [value]);

  const emit = (next) => {
    const { yy, mm, dd } = next;
    if (yy.length === 2 && mm.length === 2 && dd.length === 2) {
      const iso = `20${yy}-${mm}-${dd}`;
      const d = new Date(iso);
      if (!isNaN(d.getTime())) {
        onChange({ target: { value: iso } });
      }
    }
  };

  const focusSelect = (ref) => {
    const el = ref?.current;
    if (el) {
      el.focus();
      el.select();
    }
  };

  const handleChange = (key, max, nextRef) => (e) => {
    let v = e.target.value.replace(/\D/g, '');
    if (v.length > 2) v = v.slice(-2);

    if (v.length === 1 && key !== 'yy') {
      const d = parseInt(v, 10);
      const tensMax = Math.floor(max / 10);
      if (d > tensMax) v = '0' + v;
    }

    let advance = false;
    if (v.length === 2) {
      let n = parseInt(v, 10);
      if (n > max) v = String(max).padStart(2, '0');
      else if (key !== 'yy' && n < 1) v = '01';
      advance = true;
    }

    const next = { ...seg, [key]: v };
    setSeg(next);
    if (advance && nextRef) focusSelect(nextRef);
    emit(next);
  };

  const handleKeyDown = (key, prevRef, nextRef) => (e) => {
    if (e.key === 'Backspace' && seg[key] === '' && prevRef) {
      e.preventDefault();
      focusSelect(prevRef);
    } else if (e.key === '-' || e.key === '/' || e.key === ' ') {
      e.preventDefault();
      if (nextRef) focusSelect(nextRef);
    } else if (e.key === 'ArrowLeft' && prevRef && e.target.selectionStart === 0) {
      e.preventDefault();
      focusSelect(prevRef);
    } else if (e.key === 'ArrowRight' && nextRef && e.target.selectionEnd === seg[key].length) {
      e.preventDefault();
      focusSelect(nextRef);
    }
  };

  const segCls = 'bg-transparent text-gray-200 outline-none text-center p-0 m-0 border-0';
  const containerCls = (className || '') + ' flex items-center font-mono';

  return (
    <div className={containerCls}>
      <span className="text-gray-500 select-none">20</span>
      <input
        ref={yyRef}
        type="text"
        inputMode="numeric"
        value={seg.yy}
        onChange={handleChange('yy', 99, mmRef)}
        onFocus={(e) => e.target.select()}
        onKeyDown={handleKeyDown('yy', null, mmRef)}
        placeholder="YY"
        aria-label="Year (last two digits)"
        className={segCls}
        style={{ width: '2.2ch' }}
      />
      <span className="text-gray-500 select-none">-</span>
      <input
        ref={mmRef}
        type="text"
        inputMode="numeric"
        value={seg.mm}
        onChange={handleChange('mm', 12, ddRef)}
        onFocus={(e) => e.target.select()}
        onKeyDown={handleKeyDown('mm', yyRef, ddRef)}
        placeholder="MM"
        aria-label="Month"
        className={segCls}
        style={{ width: '2.2ch' }}
      />
      <span className="text-gray-500 select-none">-</span>
      <input
        ref={ddRef}
        type="text"
        inputMode="numeric"
        value={seg.dd}
        onChange={handleChange('dd', 31, null)}
        onFocus={(e) => e.target.select()}
        onKeyDown={handleKeyDown('dd', mmRef, null)}
        placeholder="DD"
        aria-label="Day"
        className={segCls}
        style={{ width: '2.2ch' }}
      />
    </div>
  );
}


function LoadingPanel({ phase }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
      <div className="relative w-10 h-10">
        <div className="absolute inset-0 rounded-full border-2 border-trading-green-400/20" />
        <div className="absolute inset-0 rounded-full border-2 border-t-trading-green-400 border-r-transparent border-b-transparent border-l-transparent animate-spin" />
      </div>
      <div>
        <p className="text-[13px] text-gray-200 font-medium">{phase}</p>
        <p className="text-[11px] text-gray-600 mt-1">This may take 10 – 40 seconds depending on date range</p>
      </div>
    </div>
  );
}





// ── Saved Runs ────────────────────────────────────────────────────────────────
// Browse / load / label / delete runs auto-saved by /api/backtest/run.
// The backtest is fully deterministic per (version, dte_strategy, params_hash),
// so each row is one cached result. Click a row to reload it without re-running.

function fmtRelTime(iso) {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (isNaN(t)) return iso;
  const diffMs = Date.now() - t;
  const m = Math.floor(diffMs / 60000);
  if (m < 1)    return 'just now';
  if (m < 60)   return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)   return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30)   return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function SavedRunsPanel({ dte, versionId, currentRunId, onLoad, refreshKey, inCad, cadPerUsd, onActiveVersion }) {
  const [runs,    setRuns]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [collapsed, setCollapsed] = useState(true);   // collapsed by default — the list gets long
  const [showAll, setShowAll] = useState(false);       // when expanded, show only the latest few until "show all"
  const [loadingId, setLoadingId] = useState(null);
  const MAX_SHOWN = 6;

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({ dte, limit: 50 });
      if (versionId) {
        qs.set('version', versionId);
      }
      const res = await fetch(`${API_BASE}/api/backtest/runs?${qs}`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setRuns(json.runs || []);
      if (json.active_version && onActiveVersion) {
        onActiveVersion(json.active_version);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [dte, versionId, onActiveVersion]);

  useEffect(() => { fetchRuns(); }, [fetchRuns, refreshKey]);

  const handleLoad = async (id) => {
    setLoadingId(id);
    try {
      const res = await fetch(`${API_BASE}/api/backtest/runs/${id}`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      onLoad(json);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingId(null);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this saved run?')) return;
    try {
      const res = await fetch(`${API_BASE}/api/backtest/runs/${id}`, { method: 'DELETE' });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${res.status}`);
      }
      setRuns(rs => rs.filter(r => r.id !== id));
    } catch (err) {
      setError(err.message);
    }
  };

  const startEdit = (r, e) => {
    e.stopPropagation();
    setEditingId(r.id);
    setEditValue(r.label || '');
  };

  const cancelEdit = (e) => {
    e?.stopPropagation();
    setEditingId(null);
    setEditValue('');
  };

  const saveEdit = async (id, e) => {
    e?.stopPropagation();
    try {
      const res = await fetch(`${API_BASE}/api/backtest/runs/${id}/label`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: editValue }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setRuns(rs => rs.map(r => r.id === id ? { ...r, label: json.label } : r));
      cancelEdit();
    } catch (err) {
      setError(err.message);
    }
  };

  const cellCls = 'px-1.5 text-[10px] font-mono min-w-0 truncate';

  return (
    <div className="rounded-md bg-trading-dark-900 border border-white/[0.06] px-3 py-2.5">
      <div className="flex items-center gap-2 mb-2">
        <Save className="w-3 h-3 text-gray-500" />
        <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">
          Saved Runs ({dte} DTE)
        </p>
        <span className="text-[10px] text-gray-700">
          {runs.length} run{runs.length === 1 ? '' : 's'} · auto-saved on each backtest
        </span>
        <button
          type="button"
          onClick={fetchRuns}
          disabled={loading}
          className="ml-auto p-1 hover:bg-white/[0.04] rounded transition-colors disabled:opacity-40"
          title="Refresh list"
        >
          <RefreshCw className={`w-3 h-3 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
        </button>
        <button
          type="button"
          onClick={() => setCollapsed(c => !c)}
          className="p-1 hover:bg-white/[0.04] rounded transition-colors"
          title={collapsed ? 'Expand' : 'Collapse'}
        >
          {collapsed
            ? <ChevronDown className="w-3 h-3 text-gray-500" />
            : <ChevronUp className="w-3 h-3 text-gray-500" />}
        </button>
      </div>

      {error && (
        <p className="text-[10px] text-trading-red-400 font-mono mb-2">{error}</p>
      )}

      {!collapsed && (
        <>
          {runs.length === 0 && !loading && !error && (
            <p className="text-[10px] text-gray-700 font-mono py-3 text-center">
              No saved runs yet. Run a backtest to populate this list.
            </p>
          )}

          {runs.length > 0 && (
            <div className="space-y-0.5">
              {/* Header */}
              <div className="flex items-center gap-1 px-1 py-1 text-[9px] uppercase tracking-wider text-gray-700 border-b border-white/[0.04]">
                <div style={{ flex: '1.2 0 0%' }} className={cellCls}>When</div>
                <div style={{ flex: '1.0 0 0%' }} className={cellCls}>Algo</div>
                <div style={{ flex: '2.0 0 0%' }} className={cellCls}>Range</div>
                <div style={{ flex: '1.2 0 0%' }} className={`${cellCls} text-right`}>Return</div>
                <div style={{ flex: '0.9 0 0%' }} className={`${cellCls} text-right`}
                  title="Mark-to-market for runs saved 2026-09-27 on; older runs (tagged 'cost') hold open options at cost">Max DD</div>
                <div style={{ flex: '0.6 0 0%' }} className={`${cellCls} text-right`}>N</div>
                <div style={{ flex: '1.4 0 0%' }} className={cellCls}>Final</div>
                <div style={{ flex: '2.5 0 0%' }} className={cellCls}>Label</div>
                <div style={{ flex: '0.7 0 0%' }} />
              </div>
              {(showAll ? runs : runs.slice(0, MAX_SHOWN)).map(r => {
                const isCurrent = currentRunId === r.id;
                const isLoading = loadingId === r.id;
                const isEditing = editingId === r.id;
                const retClass = r.total_return_pct >= 0
                  ? 'text-trading-green-400' : 'text-trading-red-400';
                const ddClass = r.max_dd_pct <= 50 ? 'text-trading-green-400'
                              : r.max_dd_pct <= 75 ? 'text-amber-400'
                              : 'text-trading-red-400';
                return (
                  <div
                    key={r.id}
                    onClick={() => !isEditing && !isLoading && handleLoad(r.id)}
                    className={`flex items-center gap-1 px-1 py-1 rounded cursor-pointer transition-colors
                      ${isCurrent
                        ? 'bg-trading-green-400/10 border border-trading-green-400/30'
                        : 'hover:bg-white/[0.03] border border-transparent'}`}
                  >
                    <div style={{ flex: '1.2 0 0%' }} className={`${cellCls} text-gray-500`}>
                      {isLoading ? '…' : fmtRelTime(r.run_at)}
                    </div>
                    <div
                      style={{ flex: '1.0 0 0%' }}
                      className={`${cellCls} text-gray-400`}
                      title={r.version?.git_commit
                        ? `${r.version.label} · ${r.version.git_commit}`
                        : (r.version?.label || `v${r.version_id}`)}
                    >
                      {r.version?.label || `v${r.version_id}`}
                    </div>
                    <div style={{ flex: '2.0 0 0%' }} className={`${cellCls} text-gray-400`}>
                      {r.start_date} → {r.end_date}
                    </div>
                    <div style={{ flex: '1.2 0 0%' }} className={`${cellCls} text-right ${retClass}`}>
                      {r.total_return_pct >= 0 ? '+' : ''}{fmtPct(r.total_return_pct, 1)}
                    </div>
                    <div style={{ flex: '0.9 0 0%' }} className={`${cellCls} text-right ${ddClass}`}
                      title={`Max DD — ${ddBasisLabel(r.dd_basis)}`}>
                      {r.max_dd_pct.toFixed(1)}%
                      {r.dd_basis !== 'mtm' && <span className="text-[8px] text-gray-600 ml-0.5">cost</span>}
                    </div>
                    <div style={{ flex: '0.6 0 0%' }} className={`${cellCls} text-right text-gray-500`}>
                      {r.n_trades}
                    </div>
                    <div style={{ flex: '1.4 0 0%' }} className={`${cellCls} text-gray-500`}>
                      {fmtMoney(r.final_equity, inCad, cadPerUsd)}
                    </div>
                    <div
                      style={{ flex: '2.5 0 0%' }}
                      className={`${cellCls} text-sky-400/70`}
                      onClick={isEditing ? (e) => e.stopPropagation() : undefined}
                    >
                      {isEditing ? (
                        <div className="flex items-center gap-1">
                          <input
                            value={editValue}
                            onChange={e => setEditValue(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') saveEdit(r.id, e);
                              if (e.key === 'Escape') cancelEdit(e);
                            }}
                            autoFocus
                            placeholder="label…"
                            className="bg-trading-dark-800 border border-white/[0.1] rounded text-[10px] text-gray-200 px-1 py-0.5 font-mono w-full focus:outline-none focus:border-trading-green-400/50"
                          />
                          <button
                            type="button"
                            onClick={(e) => saveEdit(r.id, e)}
                            className="p-0.5 hover:bg-white/[0.04] rounded"
                            title="Save"
                          >
                            <Check className="w-3 h-3 text-trading-green-400" />
                          </button>
                          <button
                            type="button"
                            onClick={cancelEdit}
                            className="p-0.5 hover:bg-white/[0.04] rounded"
                            title="Cancel"
                          >
                            <X className="w-3 h-3 text-gray-500" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 group">
                          <span className="truncate flex-1">{r.label || <span className="text-gray-700">—</span>}</span>
                          <button
                            type="button"
                            onClick={(e) => startEdit(r, e)}
                            className="p-0.5 hover:bg-white/[0.04] rounded opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Edit label"
                          >
                            <Pencil className="w-2.5 h-2.5 text-gray-500" />
                          </button>
                        </div>
                      )}
                    </div>
                    <div style={{ flex: '0.7 0 0%' }} className="flex items-center justify-end">
                      <button
                        type="button"
                        onClick={(e) => handleDelete(r.id, e)}
                        className="p-1 hover:bg-trading-red-400/10 rounded transition-colors"
                        title="Delete run"
                      >
                        <Trash2 className="w-3 h-3 text-gray-600 hover:text-trading-red-400" />
                      </button>
                    </div>
                  </div>
                );
              })}
              {runs.length > MAX_SHOWN && (
                <button
                  type="button"
                  onClick={() => setShowAll(s => !s)}
                  className="w-full text-[10px] text-gray-500 hover:text-gray-300 font-mono py-1.5 hover:bg-white/[0.02] rounded transition-colors"
                >
                  {showAll ? `Show fewer (latest ${MAX_SHOWN})` : `Show all ${runs.length} runs…`}
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}




// ── Advanced Params Panel ─────────────────────────────────────────────────────

function Tooltip({ text }) {
  const [show, setShow] = useState(false);
  return (
    <span className="relative inline-flex items-center ml-1" onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      <span className="w-3 h-3 rounded-full bg-white/[0.08] text-gray-600 text-[8px] font-bold flex items-center justify-center cursor-default leading-none select-none">?</span>
      {show && (
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 w-56 bg-trading-dark-800 border border-white/[0.10] rounded px-2.5 py-2 text-[10px] text-gray-300 leading-relaxed shadow-xl z-50 pointer-events-none">
          {text}
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-white/[0.10]" />
        </span>
      )}
    </span>
  );
}

// Manifest-driven advanced panel: renders every portfolio knob from
// /api/strategy/param-manifest, grouped. Editable knobs bind to `adv`; the rest
// are read-only ("cfg" badge) showing the selected profile's value — reproducible
// but applied from config. A new manifest knob auto-appears here.
function ManifestAdvancedPanel({ adv, setAdv, manifest, profileDefaults, profileKey }) {
  const [sharedOpen, setSharedOpen] = useState(false);   // the 52 common-across-profiles knobs — collapsed by default
  const inputCls = 'bg-trading-dark-800 border border-white/[0.08] rounded text-[11px] text-gray-200 px-2 py-1 focus:outline-none focus:border-trading-green-400/50 font-mono w-full';
  const roCls = 'bg-trading-dark-900 border border-white/[0.05] rounded text-[11px] text-gray-500 px-2 py-1 font-mono w-full cursor-not-allowed';
  const labelCls = 'text-[9px] font-medium uppercase tracking-wider text-gray-600 mb-0.5 flex items-center gap-1';

  if (!manifest || !manifest.length) {
    return <div className="mt-3 pt-3 border-t border-white/[0.06] text-[11px] text-gray-500">Loading strategy parameters…</div>;
  }
  const pd = (profileDefaults && profileDefaults[profileKey]) || {};

  const setVal = (key, kind) => (e) => {
    let raw;
    if (kind === 'choice') raw = e.target.value;
    else if (e.target.value === '') raw = '';
    else { const f = parseFloat(e.target.value); raw = Number.isNaN(f) ? e.target.value : f; }
    setAdv(a => ({ ...a, [key]: raw }));
  };

  const renderField = (p) => {
    if (!p.editable) {
      let v = pd[p.key];
      if (v === undefined || v === null) v = p.default;
      const disp = (typeof v === 'boolean') ? (v ? 'on' : 'off') : (v === undefined || v === null ? '' : v);
      return (
        <div key={p.key} title={`${p.tip || p.label}\n(applies from config — reproducible, per-run editing coming)`}>
          <label className={labelCls + ' opacity-50'}>
            <span className="truncate">{p.label}{p.unit ? ` ${p.unit}` : ''}</span>
            <span className="text-[7px] text-gray-700 border border-gray-700/60 rounded px-0.5 leading-none">cfg</span>
          </label>
          <input readOnly value={disp} className={roCls} />
        </div>
      );
    }
    const cur = adv[p.key];
    const v = (cur === undefined || cur === null) ? p.default : cur;
    if (p.kind === 'bool') {
      return (
        <div key={p.key} className="flex flex-col">
          <label className={labelCls}><span className="truncate">{p.label}</span>{p.tip && <Tooltip text={p.tip} />}</label>
          <div
            onClick={() => setAdv(a => ({ ...a, [p.key]: !((a[p.key] === undefined ? p.default : a[p.key])) }))}
            className={`relative w-8 h-4 rounded-full transition-colors cursor-pointer mt-0.5 ${v ? 'bg-trading-green-400' : 'bg-white/[0.12]'}`}>
            <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-transform ${v ? 'translate-x-4' : 'translate-x-0.5'}`} />
          </div>
        </div>
      );
    }
    if (p.kind === 'choice') {
      return (
        <div key={p.key}>
          <label className={labelCls}><span className="truncate">{p.label}</span>{p.tip && <Tooltip text={p.tip} />}</label>
          <select value={v} onChange={setVal(p.key, 'choice')} className={inputCls}>
            {(p.choices || []).map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      );
    }
    return (
      <div key={p.key}>
        <label className={labelCls}>
          <span className="truncate">{p.label}{p.unit ? ` ${p.unit}` : ''}</span>{p.tip && <Tooltip text={p.tip} />}
        </label>
        <input type="number" min={p.min ?? undefined} max={p.max ?? undefined} step={p.step || 1}
          value={v} onChange={setVal(p.key, p.kind)} className={inputCls} />
      </div>
    );
  };

  const renderGroup = (grp) => (
    <div key={grp.group}>
      <p className="text-[9px] font-medium uppercase tracking-wider text-gray-700 mb-1.5">{grp.group}</p>
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-8 gap-2">
        {grp.params.map(renderField)}
      </div>
    </div>
  );

  // Split each group's params into profile-specific (visible) vs shared-mechanism
  // (the 52 knobs identical across Apex/Core/Sentinel — CTSL, SAW, DTE router,
  // F3F, dead-hold, regime, slippage). The shared set goes in a collapsed
  // sub-panel so the page isn't a wall of 91 fields.
  const primaryGroups = [];
  const sharedGroups = [];
  let nShared = 0;
  for (const grp of manifest) {
    const prim = grp.params.filter(p => !p.shared);
    const shar = grp.params.filter(p => p.shared);
    if (prim.length) primaryGroups.push({ group: grp.group, params: prim });
    if (shar.length) { sharedGroups.push({ group: grp.group, params: shar }); nShared += shar.length; }
  }

  const nTotal = manifest.reduce((n, g) => n + g.params.length, 0);
  const nEditable = manifest.reduce((n, g) => n + g.params.filter(p => p.editable).length, 0);
  return (
    <div className="mt-3 pt-3 border-t border-white/[0.06] space-y-3">
      <p className="text-[9px] text-gray-600">
        Full <span className="uppercase">{profileKey}</span> strategy — {nTotal} knobs ({nEditable} editable,{' '}
        {nTotal - nEditable} <span className="text-gray-500 border border-gray-700/60 rounded px-0.5">cfg</span>-applied / reproducible).
        Editing a knob overrides it for the next run; profile toggle resets to that profile.
      </p>
      {primaryGroups.map(renderGroup)}

      {sharedGroups.length > 0 && (
        <div className="rounded-md bg-trading-dark-900/60 border border-white/[0.05]">
          <button
            type="button"
            onClick={() => setSharedOpen(o => !o)}
            className="w-full flex items-center gap-2 px-2.5 py-2 hover:bg-white/[0.03] transition-colors text-left"
          >
            {sharedOpen
              ? <ChevronUp className="w-3 h-3 text-gray-500 flex-shrink-0" />
              : <ChevronDown className="w-3 h-3 text-gray-500 flex-shrink-0" />}
            <span className="text-[10px] font-medium uppercase tracking-wider text-gray-500">
              Shared mechanism parameters
            </span>
            <span className="text-[9px] text-gray-700 truncate">
              {nShared} knobs · identical across Apex / Core / Sentinel (CTSL · SAW · DTE router · F3F · dead-hold · regime · slippage)
            </span>
          </button>
          {sharedOpen && (
            <div className="px-2.5 pb-3 pt-1 space-y-3 border-t border-white/[0.04]">
              {sharedGroups.map(renderGroup)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function Backtest() {
  const [params, setParams] = useState({
    from:          startOfYearStr(),
    to:            todayStr(),
    capital:       50000,
    min_score:     75,
    max_put_score: 25,
    calls_only:    true,   // default profile (Core) is puts-off; toggle/effect keep this synced

    flagged_only:  false,
    currency:      'CAD',
    dte:           '30',  // Phase 16 (2026-04-28): '30' or '15'
  });
  const [adv,        setAdv]        = useState(DEFAULT_ADVANCED);
  // Defaults derived from /api/strategy/config — held separately from `adv`
  // so the "Reset to defaults" button always restores the live shipped values,
  // not whatever the user happened to land on. Initialized to the fallback
  // literal; replaced when the fetch resolves.
  const [defaults,   setDefaults]   = useState(DEFAULT_ADVANCED);
  const [showAdv,    setShowAdv]    = useState(false);
  const [data,       setData]       = useState(null);
  // Schema-driven portfolio-knob manifest (all 91 knobs + per-profile defaults +
  // editable flags) from /api/strategy/param-manifest. Drives the AdvancedPanel
  // render and the run payload so a newly-surfaced knob auto-appears + auto-sends.
  const [paramManifest, setParamManifest] = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);
  const [phase,      setPhase]      = useState(PUNCHLINE_LOADING_PHASES[0]);
  const [cadPerUsd,  setCadPerUsd]  = useState(DEFAULT_CAD_PER_USD);
  // Bumped after each successful backtest run so SavedRunsPanel re-fetches
  // the list (the new run was just auto-saved server-side).
  const [savedRefreshKey, setSavedRefreshKey] = useState(0);
  // Score-version selector metadata is shared with Dashboard/Historic.
  const [activeVersion, setActiveVersion] = useState(null);
  const [availableScoreVersions, setAvailableScoreVersions] = useState([]);
  const [legacyScoreVersions, setLegacyScoreVersions] = useState([]);
  const [selectedScoreVersionId, setSelectedScoreVersionId] = useState(null);
  const [portfolioProfiles, setPortfolioProfiles] = useState(DEFAULT_PORTFOLIO_PROFILES);
  const [portfolioProfile, setPortfolioProfile] = useState(() => {
    if (typeof window === 'undefined') return 'core';
    const stored = window.localStorage?.getItem(PORTFOLIO_PROFILE_STORAGE_KEY);
    return ['sentinel', 'core', 'apex'].includes(stored) ? stored : 'core';
  });
  const timerRef = useRef(null);
  // Raw (un-profiled) shipped base config from /api/strategy/config. Lets the
  // profile toggle snap the advanced params synchronously (set-and-run) without
  // waiting on a re-fetch. Populated by the strategy-config effect below.
  const rawDefaultsRef = useRef(DEFAULT_ADVANCED);

  useEffect(() => {
    let alive = true;
    fetch(`${API_BASE}/api/score/versions`)
      .then(r => r.ok ? r.json() : null)
      .then(j => {
        if (!alive || !j) return;
        setActiveVersion(j.version || null);
        setAvailableScoreVersions(Array.isArray(j.available_versions) ? j.available_versions : []);
        setLegacyScoreVersions(Array.isArray(j.legacy_versions) ? j.legacy_versions : []);
      })
      .catch(() => { /* SavedRunsPanel will still surface the active version. */ });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    fetch(`${API_BASE}/api/portfolio/profiles/compare?_t=${Math.floor(Date.now() / 30000)}`)
      .then(r => r.ok ? r.json() : null)
      .then(j => {
        if (!alive || !Array.isArray(j?.rows)) return;
        setPortfolioProfiles(normalizeProfiles(j.rows));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const selectedScoreVersion = useMemo(() => {
    const all = [...availableScoreVersions, ...legacyScoreVersions];
    if (selectedScoreVersionId != null) {
      return all.find(v => v.id === selectedScoreVersionId) || null;
    }
    return activeVersion;
  }, [availableScoreVersions, legacyScoreVersions, selectedScoreVersionId, activeVersion]);

  const selectedPortfolioProfile = useMemo(() => {
    return normalizeProfiles(portfolioProfiles).find(profile => profile.key === portfolioProfile) || DEFAULT_PORTFOLIO_PROFILES[0];
  }, [portfolioProfiles, portfolioProfile]);

  const setPortfolioProfilePersist = useCallback((key, clear = true) => {
    setPortfolioProfile(key);
    try { window.localStorage?.setItem(PORTFOLIO_PROFILE_STORAGE_KEY, key); } catch (e) { /* ignore */ }
    if (clear) {
      // User toggle: snap the advanced params to this profile NOW (set-and-run),
      // from the raw shipped base, instead of waiting on the async strategy-config
      // effect. Skipped on programmatic calls (clear=false, e.g. loading a saved
      // run) so that run's own saved params are preserved.
      const profile = normalizeProfiles(portfolioProfiles).find((p) => p.key === key);
      if (profile) {
        const profiled = applyPortfolioProfileToAdv(rawDefaultsRef.current, profile);
        setDefaults(profiled);
        setAdv(profiled);
        // Profile also drives the main-form signal params (Calls Only + min call score).
        setParams(p => ({ ...p, ...signalParamsFromAdv(profiled) }));
      }
      setData(null);
      setError(null);
    }
  }, [portfolioProfiles]);

  const setBacktestScoreVersion = useCallback((versionId) => {
    const numeric = versionId == null || versionId === '' ? null : Number(versionId);
    setSelectedScoreVersionId(Number.isFinite(numeric) ? numeric : null);
    setData(null);
    setError(null);
  }, []);

  const setFallbackActiveVersion = useCallback((version) => {
    setActiveVersion(prev => prev || version);
  }, []);

  // Fetch shipped strategy defaults on mount, and re-fetch on DTE change.
  // strategy_config.py is the single source of truth (replaces the hard-coded
  // DEFAULT_ADVANCED for actual values; the literal stays as fallback so the
  // form is functional pre-fetch / on offline).
  useEffect(() => {
    const which = params.dte === '15' ? '15dte' : '30dte';
    fetch(`/api/strategy/config?strategy=${which}`)
      .then(r => r.ok ? r.json() : null)
      .then(j => {
        if (!j || !j[which]) return;
        const fetched = defaultsFromCfg(j[which]);
        rawDefaultsRef.current = fetched;
        const profiled = applyPortfolioProfileToAdv(fetched, selectedPortfolioProfile);
        setDefaults(profiled);
        setAdv(profiled);
        // Sync the main-form signal params to the active profile (Calls Only +
        // min call score), plus the put threshold from the shipped config.
        setParams(p => ({
          ...p,
          ...signalParamsFromAdv(profiled),
          ...(j[which].PUT_THRESHOLD != null ? { max_put_score: j[which].PUT_THRESHOLD } : {}),
        }));
      })
      .catch(() => { /* keep DEFAULT_ADVANCED fallback on fetch failure */ });
  }, [params.dte, selectedPortfolioProfile]);

  // Schema-driven knob manifest: all portfolio params + per-profile defaults +
  // editable flags. Re-fetched on DTE change. Structure is identical across
  // profiles; only `profile_defaults` differs (the panel snaps on profile toggle).
  useEffect(() => {
    let alive = true;
    const dteq = params.dte === '15' ? '15' : '30';
    fetch(`${API_BASE}/api/strategy/param-manifest?dte=${dteq}`)
      .then(r => r.ok ? r.json() : null)
      .then(j => { if (alive && j && Array.isArray(j.manifest)) setParamManifest(j); })
      .catch(() => { /* AdvancedPanel shows a loading note if absent */ });
    return () => { alive = false; };
  }, [params.dte]);

  const inCad = params.currency === 'CAD';
  const usdCapital = inCad ? Math.round(params.capital / cadPerUsd) : params.capital;

  const runBacktest = useCallback(async () => {
    setLoading(true);
    setError(null);
    setData(null);

    const shuffled = shuffledPunchlineLoadingPhases();
    let pi = 0;
    setPhase(shuffled[0]);
    timerRef.current = setInterval(() => {
      pi = (pi + 1) % shuffled.length;
      setPhase(shuffled[pi]);
    }, 3000);

    try {
      const qs = new URLSearchParams({
        from:       params.from,
        to:         params.to,
        capital:    usdCapital,
        min_score:  params.min_score,
        profile:    portfolioProfile,
        calls_only:   params.calls_only   ? 'true' : 'false',
        flagged_only: params.flagged_only ? 'true' : 'false',
      });
      // Advanced params — driven by the manifest's editable set so a newly-wired
      // editable knob auto-sends. Falls back to the known editable keys if the
      // manifest hasn't loaded (the endpoint then uses the profile/config defaults).
      const _editableKeys = (paramManifest?.manifest || [])
        .flatMap(g => g.params).filter(p => p.editable).map(p => p.key);
      const _fallbackKeys = [
        'tp_pct','tp_stress_pct','sl_pct','sl_stress_pct','put_tp_pct','put_sl_pct',
        'put_sl_hold_default','put_sl_hold_monday','breadth_adaptive','hard_sell_day','hard_sell_loss',
        'max_positions','max_positions_call','max_positions_put','practical_exposure_enabled',
        'practical_capital_ceiling','gross_premium_cap','call_premium_cap','put_premium_cap',
        'opp_sat_call_ref','opp_sat_put_ref','opp_sat_power','opp_sat_floor',
        'alloc_95plus','alloc_85_94','alloc_80_84','alloc_75_79','alloc_70_74',
        'alloc_p15','alloc_p16_20','alloc_p21_25','dd_soft_lo','dd_soft_hi','dd_soft_floor',
        'rxdd_enabled','rxdd_vix_c','rxdd_vix_w','rxdd_depth','rxdd_dd_min',
        'mwdd_enabled','mwdd_mcc_c','mwdd_mcc_w','mwdd_depth','mwdd_dd_min','mwdd_vix_panic',
        'tvdd_enabled','tvdd_trin_c','tvdd_trin_w','tvdd_depth','tvdd_dd_min','tvdd_vix_panic',
        'bdiv_enabled','bdiv_prox_cut','bdiv_prox_full','bdiv_gap_c','bdiv_gap_w','bdiv_depth',
        'svr_enabled','svr_lo_cut','svr_lo_full','svr_hi_full','svr_hi_cut','svr_floor',
        'spread_tilt_enabled','spread_tilt_lo','spread_tilt_hi','spread_tilt_depth',
      ];
      const _sendKeys = _editableKeys.length ? _editableKeys : _fallbackKeys;
      for (const k of _sendKeys) {
        let v = adv[k];
        if (v === undefined || v === null || v === '') continue;
        if (typeof v === 'boolean') v = v ? 'true' : 'false';
        qs.set(k, String(v));
      }
      if (!params.calls_only) {
        qs.set('max_put_score', params.max_put_score);
      }
      if (params.dte === '15') {
        qs.set('dte', '15');
      }
      if (selectedScoreVersionId) {
        qs.set('version', selectedScoreVersionId);
      }
      const res  = await fetch(`${API_BASE}/api/backtest/run?${qs}`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setData(json);
      if (json.available_versions) {
        setAvailableScoreVersions(json.available_versions);
      }
      if (json.legacy_versions) {
        setLegacyScoreVersions(json.legacy_versions);
      }
      if (json.params?.cad_usd_rate) {
        setCadPerUsd(parseFloat((1 / json.params.cad_usd_rate).toFixed(4)));
      }
      // Tell SavedRunsPanel to re-fetch — the new run was auto-saved server-side.
      setSavedRefreshKey(k => k + 1);
    } catch (e) {
      setError(e.message);
    } finally {
      clearInterval(timerRef.current);
      setLoading(false);
    }
  }, [params, usdCapital, adv, selectedScoreVersionId, portfolioProfile]);

  // Load a previously-saved run from /api/backtest/runs/<id>. The saved
  // payload has the same shape as a live /api/backtest/run response, so we
  // just slot it into `data`.
  const handleLoadSavedRun = useCallback((json) => {
    setData(json);
    setError(null);
    if (json.version?.id) {
      setSelectedScoreVersionId(
        activeVersion?.id && json.version.id === activeVersion.id
          ? null
          : Number(json.version.id)
      );
    }
    if (json.params?.cad_usd_rate) {
      setCadPerUsd(parseFloat((1 / json.params.cad_usd_rate).toFixed(4)));
    }
    if (json.params?.portfolio_profile) {
      setPortfolioProfilePersist(json.params.portfolio_profile, false);
    }
  }, [activeVersion, setPortfolioProfilePersist]);

  const set = (key) => (e) =>
    setParams(p => ({ ...p, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const inputCls = 'bg-trading-dark-900 border border-white/[0.08] rounded text-[12px] text-gray-200 px-2 py-1.5 focus:outline-none focus:border-trading-green-400/50 font-mono w-full';
  const labelCls = 'text-[10px] font-medium uppercase tracking-wider text-gray-600 mb-1 block';

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-[1200px] mx-auto px-4 py-4 space-y-4">

        {/* Page header */}
        <div className="flex items-center border-b border-white/[0.06] pb-3">
          <div className="flex items-center gap-2">
            <BarChart2 className="w-4 h-4 text-trading-green-300 flex-shrink-0" />
            <h2 className="text-[13px] font-semibold text-gray-200">Backtest</h2>
            <span className="text-[11px] text-gray-600 ml-1">
              Cascade allocation strategy · ATM options
            </span>
          </div>

          {/* DTE strategy toggle — floats centered between title and version */}
          <div
            className="mx-auto flex items-center rounded overflow-hidden border border-white/[0.08] text-[11px] font-medium leading-none"
            title="Strategy: 30 DTE primary / 15 DTE research"
          >
            {[{key: '30', label: '30 DTE Primary'}, {key: '15', label: '15 DTE Research'}].map(({key, label}) => (
              <button
                key={key}
                onClick={() => setParams(p => ({ ...p, dte: key }))}
                className={`px-3 py-1 transition-colors ${
                  params.dte === key
                    ? 'bg-trading-blue-400/20 text-trading-blue-300'
                    : 'text-gray-600 hover:text-gray-400'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <PortfolioProfileToggle
            profiles={portfolioProfiles}
            value={portfolioProfile}
            onChange={setPortfolioProfilePersist}
            compact
          />

          <ScoreVersionSelector
            versions={availableScoreVersions}
            legacyVersions={legacyScoreVersions}
            currentVersion={selectedScoreVersion}
            activeVersionId={activeVersion?.id}
            selectedVersionId={selectedScoreVersionId}
            onSelect={setBacktestScoreVersion}
            ariaLabel="Select backtest score version"
            titlePrefix="Backtest scores"
          />
        </div>

        {/* Params form */}
        <div className="rounded-md bg-trading-dark-900 border border-white/[0.06] px-3 py-3">
          {/* Basic params row */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 items-end">

            <div>
              <label className={labelCls}>From</label>
              <SegmentedDateInput value={params.from} onChange={set('from')} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>To</label>
              <SegmentedDateInput value={params.to} onChange={set('to')} className={inputCls} />
            </div>

            {/* Capital */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className={labelCls + ' mb-0'}>Capital</label>
                <div className="flex items-center rounded overflow-hidden border border-white/[0.08] text-[9px] font-medium leading-none">
                  {['CAD', 'USD'].map(cur => (
                    <button
                      key={cur}
                      onClick={() => setParams(p => ({ ...p, currency: cur }))}
                      className={`px-1.5 py-0.5 transition-colors ${
                        params.currency === cur
                          ? 'bg-trading-green-400/20 text-trading-green-300'
                          : 'text-gray-600 hover:text-gray-400'
                      }`}
                    >
                      {cur}
                    </button>
                  ))}
                </div>
              </div>
              <div className={inputCls + ' flex items-center'}>
                <span className="text-gray-500 select-none">{inCad ? 'C$' : '$'}</span>
                <input
                  type="number" min="1" step="1"
                  value={params.capital ? params.capital / 1000 : ''}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    setParams(p => ({ ...p, capital: isNaN(v) ? 0 : v * 1000 }));
                  }}
                  onFocus={(e) => e.target.select()}
                  className="bg-transparent text-gray-200 outline-none text-right p-0 m-0 border-0 flex-1 min-w-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <span className="text-gray-500 select-none">,000</span>
              </div>
            </div>

            <div>
              <label className={labelCls}>Min Call Score</label>
              <input type="number" min="50" max="99" step="1"
                value={params.min_score} onChange={set('min_score')} className={inputCls} />
            </div>

            {!params.calls_only ? (
              <div>
                <label className={labelCls}>Max Put Score</label>
                <input type="number" min="1" max="49" step="1"
                  value={params.max_put_score} onChange={set('max_put_score')} className={inputCls} />
              </div>
            ) : (
              <div />
            )}

            <div className="flex flex-col justify-end pb-0.5 gap-2">
              {[
                { key: 'calls_only',   label: 'Calls Only'   },
                { key: 'flagged_only', label: 'Flagged Only' },
              ].map(({ key, label }) => (
                <label key={key} className="flex items-center justify-between gap-2 cursor-pointer select-none w-full">
                  <span className="text-[10px] font-medium uppercase tracking-wider text-gray-600">{label}</span>
                  <div
                    onClick={() => setParams(p => ({ ...p, [key]: !p[key] }))}
                    className={`relative w-8 h-4 rounded-full transition-colors cursor-pointer flex-shrink-0 ${
                      params[key] ? 'bg-trading-green-400' : 'bg-white/[0.12]'
                    }`}
                  >
                    <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-transform ${
                      params[key] ? 'translate-x-4' : 'translate-x-0.5'
                    }`} />
                  </div>
                </label>
              ))}
            </div>

            <div className="flex items-end">
              <button
                onClick={runBacktest}
                disabled={loading}
                className={`w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded text-[12px] font-medium transition-colors ${
                  loading
                    ? 'bg-trading-green-400/10 text-trading-green-400/40 cursor-not-allowed'
                    : 'bg-trading-green-400/15 text-trading-green-300 hover:bg-trading-green-400/25 border border-trading-green-400/20'
                }`}
              >
                {loading
                  ? <><Activity className="w-3.5 h-3.5 animate-pulse" /> Running…</>
                  : <><Play className="w-3.5 h-3.5" /> Run</>
                }
              </button>
            </div>
          </div>

          {/* Advanced accordion toggle */}
          <button
            onClick={() => setShowAdv(s => !s)}
            className="mt-3 flex items-center gap-1.5 text-[10px] font-medium text-gray-600 hover:text-gray-400 transition-colors"
          >
            {showAdv ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            Advanced parameters
          </button>

          {showAdv && <ManifestAdvancedPanel adv={adv} setAdv={setAdv}
            manifest={paramManifest?.manifest}
            profileDefaults={paramManifest?.profile_defaults}
            profileKey={portfolioProfile} />}

          {/* Strategy reminder (collapsed summary) */}
          {!showAdv && (
            <div className="mt-2.5 pt-2.5 border-t border-white/[0.04] flex flex-wrap gap-x-4 gap-y-1">
              {[
                `TP +${adv.tp_pct}/${adv.tp_stress_pct}% · SL −${adv.sl_pct}/${adv.sl_stress_pct}%${adv.breadth_adaptive ? ' (breadth-adaptive)' : ' (fixed)'}`,
                `Hard sell day ${adv.hard_sell_day}`,
                `Max ${adv.max_positions} positions · C/P ${adv.max_positions_call}/${adv.max_positions_put}`,
                `95+→${adv.alloc_95plus}% · 85-94/80-84/75-79→${adv.alloc_85_94}/${adv.alloc_80_84}/${adv.alloc_75_79}%`,
                `Puts ≤15→${adv.alloc_p15}% · 16-20→${adv.alloc_p16_20}% · 21-25→${adv.alloc_p21_25}%`,
                adv.practical_exposure_enabled
                  ? `PX cap ${fmtMoney(adv.practical_capital_ceiling || 0, false)} · gross/call/put ${adv.gross_premium_cap}/${adv.call_premium_cap}/${adv.put_premium_cap}% · refs ${adv.opp_sat_call_ref}/${adv.opp_sat_put_ref}`
                  : `PX cap OFF`,
                adv.breadth_alloc_enabled
                  ? `F3f alloc scale: calls ${adv.f3f_call_floor.toFixed(2)}–1.0 (brd ${adv.f3f_call_low}-${adv.f3f_call_thresh}) · puts ${adv.f3f_put_floor.toFixed(2)}–1.0 (brd ${adv.f3f_put_thresh}-${adv.f3f_put_high})`
                  : `F3f alloc scale OFF — legacy regime path`,
              ].map(s => (
                <span key={s} className="text-[9px] text-gray-700 font-mono">{s}</span>
              ))}
            </div>
          )}
        </div>

        {/* Saved runs (auto-saved on every successful backtest) */}
        <SavedRunsPanel
          dte={params.dte}
          versionId={selectedScoreVersionId}
          currentRunId={data?.run_id}
          onLoad={handleLoadSavedRun}
          refreshKey={savedRefreshKey}
          inCad={inCad}
          cadPerUsd={cadPerUsd}
          onActiveVersion={selectedScoreVersionId ? null : setFallbackActiveVersion}
        />

        {/* Loading */}
        {loading && <LoadingPanel phase={phase} />}

        {/* Error */}
        {!loading && error && (
          <div className="rounded-md bg-trading-red-400/5 border border-trading-red-400/20 px-4 py-3">
            <p className="text-[12px] text-trading-red-400 font-medium">{error}</p>
          </div>
        )}

        {/* Results */}
        {!loading && data && <Results data={data} inCad={inCad} cadPerUsd={cadPerUsd} />}

        {/* Empty state */}
        {!loading && !data && !error && (
          <div className="flex flex-col items-center justify-center py-20 text-center gap-3">
            <Clock className="w-8 h-8 text-gray-700" />
            <p className="text-[13px] text-gray-600">Configure parameters above and click Run to start the backtest.</p>
          </div>
        )}

      </div>
    </div>
  );
}
